<script lang="ts">
    import Modal from "@/components/Modal.svelte";
    import RelatedSingle from "@/components/Inputs/RelatedSingle.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import CustomerTable from "@/components/customers/CustomerTable.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type {Field} from "@/types/types";
    import {toast} from "svelte-sonner";
    import {useForm} from "@inertiajs/svelte";
    import type Site from '@/schemas/public/Site.ts';
    import Debug from "@/components/Debug.svelte";
    import MultiRelated from "@/components/Inputs/MultiRelated.svelte";
    import {SvelteMap, SvelteSet} from "svelte/reactivity";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import { parseFieldOptions } from '@/lib/fieldUtils';

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        site: Site;
    };

    let {blocks, show = $bindable(), site}: Props = $props();

    function closeEditSiteModal() {
        show = false;
    }

    let formInner: Record<string, any> = {};
    let customerLabel: string | null | undefined = $state(null);

    for (const block of blocks) {
        for (const field of block.fields) {
            formInner[field.column_name] = field.value;

            if (field.ui_type === 'related') {
                formInner[field.column_name + "_entity_label"] = field.value_label;
                customerLabel = field.value_label;
            }
        }
    }
    let form = $state(useForm(formInner));


    function submitEditSiteForm(e) {
        $form.processing = true;
        e.preventDefault();
        $form.put(route('sites.update', {site: site.id}), {
            onSuccess: () => {
                closeEditSiteModal();
                $form.reset();
                toast.success("Site created successfully!");
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error("Error editing site. " + message as string);
                }
            }
        });
    }

    let isSelectCustomerOpen = $state(false);
    let selectedRelatedCustomers = $state(new SvelteMap());
    site.customers.forEach(c => {
        selectedRelatedCustomers.set(c.id, c.name);
    });

    $form.customers = selectedRelatedCustomers.keys().toArray();
    customerLabel = Array.from(selectedRelatedCustomers.values()).join(", ");
</script>

{#if show}
    <Modal title="Edit Site" onClose={closeEditSiteModal}>
        <FormContainer>
            <form onsubmit={submitEditSiteForm}>
                {#each blocks as block}
                    <FormBlock label={block.label}>
                        {#each block.fields as field}
                            {#if field.editable}
                                {#if field.ui_type === 'related'}
                                    <RelatedSingle
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        bind:isOpen={isSelectCustomerOpen}
                                        bind:valLabel={customerLabel}
                                    >
                                        <CustomerTable
                                            view="related"
                                            onRowClick={(row) => {
                                                $form.customer_id = row?.id ?? null;
                                                customerLabel = row?.first_name ?? '';
                                                isSelectCustomerOpen = false;
                                            }}
                                        />
                                    </RelatedSingle>
                                    {:else if field.ui_type === 'multi_related'}
                                        <MultiRelated
                                            label={field.label}
                                            bind:value={$form[field.column_name]}
                                            error={$form.errors[field.column_name]}
                                            bind:isOpen={isSelectCustomerOpen}
                                            bind:valLabel={customerLabel}
                                        >
                                            <CustomerTable
                                                view="multi_related"
                                                onRowClick={(row) => {
                                                    // $form.customer_id = row?.id ?? null;
                                                    // customerLabel = row?.first_name ?? '';
                                                    // isSelectCustomerOpen = false;
                                                }}
                                                bind:selectedCustomers={selectedRelatedCustomers}
                                                onSubmitClick={() => {
                                                    $form.customers = selectedRelatedCustomers.keys().toArray();
                                                    customerLabel = Array.from(selectedRelatedCustomers.values()).join(", ");
                                                    isSelectCustomerOpen = false;
                                                }}
                                            />
                                        </MultiRelated>
                                    {:else if field.ui_type === 'select'}
                                        <Select
                                            label={field.label}
                                            bind:value={$form[field.column_name]}
                                            options={parseFieldOptions(field)}
                                            error={$form.errors[field.column_name]}
                                        />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else}
                                    <Input
                                        field={field}
                                        bind:value={$form[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={closeEditSiteModal}>Cancel</Button>
                    <Button type="submit" disabled={$form.processing}>
                        {#if $form.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
